import React, { useState } from "react";
import { trackQuote } from "@/lib/track";
import { scrollToId } from "@/lib/scrollToId";
import {
  Home,
  Building2,
  Warehouse,
  Leaf,
  Car,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  RotateCcw,
  Droplets,
  Layers,
  Zap,
  Factory,
} from "lucide-react";

interface BuildType {
  id: string;
  label: string;
  icon: React.ElementType;
}

const buildTypes: BuildType[] = [
  { id: "newbuild", label: "New Build / Self-Build", icon: Home },
  { id: "extension", label: "Extension / Renovation", icon: Building2 },
  { id: "heritage", label: "Heritage / Period Property", icon: Leaf },
  { id: "garage", label: "Garage / Workshop", icon: Car },
  { id: "commercial", label: "Commercial / Warehouse", icon: Warehouse },
  { id: "industrial", label: "Industrial / Heavy Duty", icon: Factory },
];

interface Recommendation {
  system: string;
  tagline: string;
  minDepth: string;
  walkOn: string;
  drying: string;
  reasons: string[];
}

const getRecommendation = (
  buildType: string,
  hasUFH: boolean,
): Recommendation => {
  if (hasUFH) {
    return {
      system: "Underfloor Heating Screed",
      tagline: "Cemfloor Therm, designed specially for underfloor heating",
      minDepth: "35mm over pipes",
      walkOn: "24 to 48 hours",
      drying: "Warms rooms quickly",
      reasons: [
        "Wraps completely around your heating pipes with no air gaps",
        "Heat reaches your room faster, so you spend less on energy",
        "Laser-levelled for a perfectly smooth floor",
        "10-year Cemfloor product guarantee for complete peace of mind",
      ],
    };
  }

  const base: Record<string, Recommendation> = {
    newbuild: {
      system: "Heavy-Duty High-Strength Screed",
      tagline: "Cemfloor C30, strong all-round screed for new homes",
      minDepth: "25mm",
      walkOn: "24 to 48 hours",
      drying: "No sanding needed",
      reasons: [
        "Strong enough to stop surface cracking in busy homes",
        "No dusty layer forms as it dries, saving time on prep",
        "We can pump up to 1,000m squared in a day to keep your build moving",
        "10-year Cemfloor product guarantee for total peace of mind",
      ],
    },
    extension: {
      system: "Rapid-Drying Screed",
      tagline: "Cemfloor Fast-Dry, dries fast so you can fit your floor sooner",
      minDepth: "25mm",
      walkOn: "24 hours",
      drying: "Ready for flooring in 10 to 14 days",
      reasons: [
        "Dries up to 60% faster than standard screed",
        "We test the moisture level on site before you fit your floor",
        "Thin layer is great for going over existing floors",
        "Laser-levelled for a perfectly flat finish",
      ],
    },
    garage: {
      system: "Heavy-Duty High-Strength Screed",
      tagline: "Cemfloor C30, tough enough for cars and vans",
      minDepth: "50mm",
      walkOn: "24 to 48 hours",
      drying: "Handles heavy loads",
      reasons: [
        "Strong enough to take the weight of vehicles",
        "Dense mix resists chipping and wear",
        "No sanding needed before you paint or coat the floor",
        "Great for garages, workshops and light commercial use",
      ],
    },
    commercial: {
      system: "Ready-To-Tile Screed",
      tagline: "Cemfloor Non-Sanding, ready for tiling straight away",
      minDepth: "25mm",
      walkOn: "24 to 48 hours",
      drying: "Ready for floor coverings",
      reasons: [
        "No dusty layer forms, so no costly sanding needed",
        "Tilers can prime and lay tiles or vinyl straight on top",
        "Mix designed to stop cracking and curling",
        "Perfectly flat finish to the highest standard",
      ],
    },
  };

  return base[buildType] ?? base.newbuild;
};

export const ScreedFinder: React.FC = () => {
  const [step, setStep] = useState(0);
  const [buildType, setBuildType] = useState<string | null>(null);
  const [hasUFH, setHasUFH] = useState<boolean | null>(null);

  const reset = () => {
    setStep(0);
    setBuildType(null);
    setHasUFH(null);
  };

  const recommendation =
    buildType && hasUFH !== null ? getRecommendation(buildType, hasUFH) : null;

  return (
    <section className="py-20 bg-[#171614] text-white border-b border-stone-800">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-xs sm:text-sm uppercase tracking-widest text-[#C9A84C] font-bold block mb-2">
            SCREED FINDER
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-4">
            Which Screed{" "}
            <span className="gold-gradient-text">Do You Need?</span>
          </h2>
          <p className="text-stone-300 text-base sm:text-lg leading-relaxed">
            Answer two quick questions and we'll suggest the right screed for
            your project, including how thick it needs to be and how long it
            takes to dry.
          </p>
        </div>

        {/* Progress Dots */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`h-2 rounded-full transition-all duration-300 ${
                step >= i ? "w-10 bg-[#C9A84C]" : "w-2 bg-stone-700"
              }`}
            />
          ))}
        </div>

        {/* Card */}
        <div className="bg-[#1C1B18] border border-stone-800 rounded-2xl shadow-2xl p-6 sm:p-10 min-h-[340px] flex flex-col">
          {/* Step 0: Build Type */}
          {step === 0 && (
            <div className="flex-1 flex flex-col">
              <h3 className="text-xl font-bold text-white mb-1">
                What type of project is it?
              </h3>
              <p className="text-stone-400 text-sm mb-6">
                Select the option that best describes your build.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {buildTypes.map((bt) => {
                  const Icon = bt.icon;
                  const selected = buildType === bt.id;
                  return (
                    <button
                      key={bt.id}
                      onClick={() => {
                        setBuildType(bt.id);
                        setStep(1);
                      }}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border text-center transition-all ${
                        selected
                          ? "bg-[#C9A84C]/15 border-[#C9A84C] text-white"
                          : "bg-[#22201D] border-stone-700/60 text-stone-300 hover:border-[#C9A84C]/50 hover:text-white"
                      }`}
                    >
                      <Icon className="w-6 h-6 text-[#C9A84C]" />
                      <span className="text-xs font-semibold leading-tight">
                        {bt.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 1: UFH */}
          {step === 1 && (
            <div className="flex-1 flex flex-col">
              <h3 className="text-xl font-bold text-white mb-1">
                Will there be underfloor heating?
              </h3>
              <p className="text-stone-400 text-sm mb-6">
                This helps us pick the screed that transfers heat best.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md mx-auto w-full">
                <button
                  onClick={() => {
                    setHasUFH(true);
                    setStep(2);
                  }}
                  className="flex flex-col items-center gap-3 p-6 rounded-xl border bg-[#22201D] border-stone-700/60 text-stone-200 hover:border-[#C9A84C] hover:text-white transition-all"
                >
                  <Droplets className="w-8 h-8 text-[#C9A84C]" />
                  <span className="font-bold text-sm">Yes, UFH installed</span>
                  <span className="text-xs text-stone-400 text-center">
                    Pipes laid or planned in the floor build-up
                  </span>
                </button>
                <button
                  onClick={() => {
                    setHasUFH(false);
                    setStep(2);
                  }}
                  className="flex flex-col items-center gap-3 p-6 rounded-xl border bg-[#22201D] border-stone-700/60 text-stone-200 hover:border-[#C9A84C] hover:text-white transition-all"
                >
                  <Layers className="w-8 h-8 text-[#C9A84C]" />
                  <span className="font-bold text-sm">No, standard floor</span>
                  <span className="text-xs text-stone-400 text-center">
                    No underfloor heating in this build
                  </span>
                </button>
              </div>
              <div className="mt-6">
                <button
                  onClick={() => setStep(0)}
                  className="inline-flex items-center gap-1.5 text-stone-400 hover:text-[#C9A84C] text-sm font-medium transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Recommendation */}
          {step === 2 && recommendation && (
            <div className="flex-1 flex flex-col">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-6 h-6 text-[#C9A84C]" />
                <h3 className="text-xl font-bold text-white">
                  Your Recommended System
                </h3>
              </div>

              <div className="bg-gradient-to-r from-stone-900 via-[#22201D] to-stone-900 border border-[#C9A84C]/30 rounded-xl p-5 mb-6">
                <span className="text-xs font-bold uppercase tracking-widest text-[#C9A84C] block mb-1">
                  Recommended
                </span>
                <h4 className="text-2xl font-extrabold text-white mb-1">
                  {recommendation.system}
                </h4>
                <p className="text-stone-400 text-sm">
                  {recommendation.tagline}
                </p>
              </div>

              {/* Spec row */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-[#22201D] border border-stone-700/60 rounded-lg p-3 text-center">
                  <span className="block text-xs text-stone-400 mb-0.5">
                    Min Depth
                  </span>
                  <span className="block text-sm font-bold text-white">
                    {recommendation.minDepth}
                  </span>
                </div>
                <div className="bg-[#22201D] border border-stone-700/60 rounded-lg p-3 text-center">
                  <span className="block text-xs text-stone-400 mb-0.5">
                    Walk-on
                  </span>
                  <span className="block text-sm font-bold text-white">
                    {recommendation.walkOn}
                  </span>
                </div>
                <div className="bg-[#22201D] border border-stone-700/60 rounded-lg p-3 text-center">
                  <span className="block text-xs text-stone-400 mb-0.5">
                    Drying
                  </span>
                  <span className="block text-sm font-bold text-white leading-tight">
                    {recommendation.drying}
                  </span>
                </div>
              </div>

              {/* Reasons */}
              <ul className="space-y-2.5 mb-6">
                {recommendation.reasons.map((r) => (
                  <li key={r} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-5 h-5 text-[#C9A84C] shrink-0 mt-0.5" />
                    <span className="text-stone-300 text-sm leading-relaxed">
                      {r}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row items-center gap-3 mt-auto">
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    trackQuote("screed_finder_result");
                    scrollToId("#contact");
                  }}
                  className="inline-flex items-center justify-center gap-2 bg-[#C9A84C] hover:bg-[#b89539] text-stone-950 font-bold text-sm px-6 py-3 rounded-lg transition-all shadow-md w-full sm:w-auto"
                >
                  <span>Get a Quote for This System</span>
                  <ArrowRight className="w-4 h-4" />
                </a>
                <button
                  onClick={reset}
                  className="inline-flex items-center justify-center gap-2 text-stone-400 hover:text-[#C9A84C] text-sm font-medium transition-colors w-full sm:w-auto"
                >
                  <RotateCcw className="w-4 h-4" />
                  Start Over
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
